import { observer } from 'mobx-react-lite';
import React, { useRef } from 'react';
import type { ApplicationSdk } from '../../core/sdk';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import SearchField from '../../components/SearchField';
import { Paginator } from 'primereact/paginator';
import { Toolbar } from 'primereact/toolbar';
import { Chip } from 'primereact/chip';
import { OverlayPanel } from 'primereact/overlaypanel';
import type BaseOverviewStore from '../stores/baseOverviewStore';
import { formatPaginationText } from '../../utils/helper';

interface MatenEntity {
    singular?: string;
    plural?: string;
}

interface MatenPagination {
    rows?: number;
    template?: string;
    currentPageReportTemplate?: string;
}
interface MatenTableColumn {
    field?: string;
    header: string;
    sortable?: boolean;
    body?: (rowData: any) => React.ReactNode;
    style?: React.CSSProperties;
    className?: string;
}

interface MatenTableAction {
    icon: string;
    tooltip?: string;
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right' | 'mouse';
    onClick: (rowData: any) => void;
    className?: string;
    onTestId?: (rowData: any) => string | undefined;
    onVisible?: (rowData: any) => boolean;
    onDisabled?: (rowData: any) => boolean;
    testId?: string;
}

interface MatenTableSearch {
    placeholder?: string;
}

interface MatenFilter {
    title?: string;
    property: string;
    removeFilter: (store: any) => void;
    chipContent?: (filter: any) => React.ReactNode;
    filterComponent: (store: any) => React.ReactNode;
}

interface MatenChip {
    label: string;
    onRemove?: (store: any) => void;
}

interface MatenTableProps {
    data: any[];
    store: BaseOverviewStore;
    sdk?: ApplicationSdk;
    columns: MatenTableColumn[];
    actions?: MatenTableAction[];
    emptyMessage?: string;
    entity?: MatenEntity; // For pagination text
    testId?: string;
    toolbarContent?: React.ReactNode;
    search?: MatenTableSearch;
    filter?: MatenFilter[];
    additionalChips?: MatenChip[];
    pagination?: boolean | MatenPagination;
    rowClassName?: (data: any) => string;
}

const MatenFilterChips = observer(
    ({
        filter,
        store,
        additionalChips,
        testId,
    }: {
        filter?: MatenFilter[];
        store: any;
        additionalChips?: MatenChip[];
        testId: string;
    }) => {
        const selectedSearch = store?.searchValue || '';
        return (
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {(filter || [])
                    .filter(
                        (filter: any) =>
                            filter.chipContent && store?.[filter.property as keyof typeof store]
                    )
                    .map((filter: any, i: number) => {
                        return (
                            <Chip
                                key={`filter-${i}`}
                                label={
                                    filter.chipContent(
                                        store?.[filter.property as keyof typeof store]
                                    ) || {}
                                }
                                removable
                                onRemove={() => filter?.removeFilter(store)}
                                data-testid={`${testId}-filter-chip-${i}`}
                            />
                        );
                    })}
                {(additionalChips || []).map((chip: any, i: number) => {
                    return (
                        <Chip
                            key={`additional-filter-${i}`}
                            label={chip.label}
                            removable
                            onRemove={() => chip.onRemove?.(store)}
                            data-testid={`${testId}-additional-filter-chip-${i}`}
                        />
                    );
                })}
                {selectedSearch && (
                    <Chip
                        label={`Suche: ${selectedSearch}`}
                        removable
                        onRemove={(_event: any) => {
                            store?.clearSearch();
                            return true;
                        }}
                        data-testid={`${testId}-filter-search-chip`}
                    />
                )}
            </div>
        );
    }
);

const MatenTable = observer((props: MatenTableProps) => {
    const {
        data,
        store,
        columns,
        actions = [],
        emptyMessage = 'Keine Einträge gefunden',
        entity = { singular: 'Eintrag', plural: 'Einträge' },
        testId = 'data-table',
        toolbarContent = null,
        search,
        pagination,
        filter,
        additionalChips,
        rowClassName,
    } = props;

    const overlayRef = useRef<any>(null);

    const MatenFilterPopover = observer(() => {
        return (
            <div
                style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}
                onClick={e => e.stopPropagation()} // Prevent event bubbling
                data-testid={`${testId}-filter-popover`}
            >
                {(filter || []).map((filter: any, i: number) => {
                    return (
                        <React.Fragment key={`filter-${i}`}>
                            {filter.title && (
                                <h4 style={{ marginBottom: '1rem', width: '100%' }}>
                                    {filter.title}
                                </h4>
                            )}
                            {filter.filterComponent(store)}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    });

    // Create search toolbar
    const SearchToolbar = () => {
        return (
            <>
                <Toolbar
                    start={() => toolbarContent || null}
                    // start={() => <EmployeeManager sdk={sdk} />}
                    center={() =>
                        filter ? (
                            <>
                                <Button
                                    icon="pi pi-filter"
                                    className="p-button-text"
                                    label="Filter"
                                    onClick={(e: any) => overlayRef.current?.toggle(e)}
                                    aria-label="Filter"
                                    data-testid={`${testId}-filter-button`}
                                />
                            </>
                        ) : null
                    }
                    end={() =>
                        search ? (
                            <SearchField
                                store={store}
                                placeholder={search.placeholder || 'durchsuche...'}
                                testId={testId}
                            />
                        ) : null
                    }
                    data-testid={`${testId}-toolbar`}
                />
                <MatenFilterChips
                    filter={filter}
                    store={store}
                    additionalChips={additionalChips}
                    testId={testId}
                />
            </>
        );
    };

    if (!store) return null;

    return (
        <div
            className="card"
            data-testid={testId}
        >
            <DataTable
                value={data}
                emptyMessage={emptyMessage}
                header={<SearchToolbar />}
                onSort={(event: any) => {
                    store.onSort(event.sortField);
                    return event;
                }}
                sortOrder={store.sortOrder === 1 ? 1 : -1}
                sortField={store.sortField}
                rowHover={true}
                loading={!!store.listLoading}
                rowClassName={rowClassName}
                data-testid={`${testId}-datatable`}
            >
                {columns.map((col, index) => (
                    <Column
                        key={`${col.field || 'action'}-${index}`}
                        field={col.field}
                        header={col.header}
                        sortable={col.sortable}
                        body={col.body}
                        style={col.style}
                        className={col.className}
                    />
                ))}
                {actions.length > 0 && (
                    <Column
                        header=""
                        body={(rowData: any) => (
                            <div className="flex gap-2">
                                {actions.map((action, index) => {
                                    return action?.onVisible?.(rowData) === false ? null : (
                                        <Button
                                            key={index}
                                            icon={action.icon}
                                            rounded
                                            text
                                            aria-label={action.tooltip || 'Action'}
                                            onClick={() => action.onClick(rowData)}
                                            className={action.className}
                                            disabled={
                                                action.onDisabled
                                                    ? action.onDisabled(rowData)
                                                    : false
                                            }
                                            data-testid={
                                                action.testId ||
                                                (action.onTestId
                                                    ? action.onTestId(rowData)
                                                    : undefined)
                                            }
                                            tooltip={action.tooltip}
                                            tooltipOptions={{
                                                position: action?.tooltipPosition || 'top',
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    />
                )}
            </DataTable>
            {filter && (
                <OverlayPanel
                    ref={overlayRef}
                    style={{ width: '300px' }}
                >
                    <MatenFilterPopover />
                </OverlayPanel>
            )}
            {pagination && (
                <Paginator
                    first={
                        ((store.currentPage || 1) - 1) *
                        (typeof pagination === 'object' && pagination.rows ? pagination.rows : 30)
                    }
                    rows={typeof pagination === 'object' && pagination.rows ? pagination.rows : 30}
                    totalRecords={store.totalCount || 1}
                    onPageChange={(event: any) => store.onPageChange(event.page + 1)}
                    template={
                        typeof pagination === 'object' && pagination.template
                            ? pagination.template
                            : 'PrevPageLink CurrentPageReport NextPageLink'
                    }
                    currentPageReportTemplate={formatPaginationText(
                        store.totalCount || 0,
                        {
                            singular: entity?.singular || 'Eintrag',
                            plural: entity?.plural || 'Einträge',
                        },
                        typeof pagination === 'object' && pagination.currentPageReportTemplate
                            ? pagination.currentPageReportTemplate
                            : 'Seite {currentPage} / {totalPages}'
                    )}
                    data-testid={`matentable-paginator`}
                />
            )}
        </div>
    );
});

export default MatenTable;
export type { MatenTableProps, MatenTableColumn, MatenTableAction, MatenFilter, MatenChip };
